/**
 * Sprint T-16.1 · Automation Center · MetricsTab.
 *
 * Counters + p50/p95/p99 por job · auto-refresh 30s (§17.4).
 */
import React from "react";
import { WidgetCard, MetricCard, WidgetGrid, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useAutomationMetrics } from "../../hooks/useAutomation";
import { AbsoluteTime, RelativeTime } from "./shared/Formatters";

function _latencyColor(p95) {
  if (p95 == null) return "text-[var(--ag-text-dim)]";
  if (p95 < 500) return "text-emerald-500";
  if (p95 < 2000) return "text-amber-500";
  return "text-red-500";
}

function CountersStrip({ counts = {} }) {
  const keys = ["started", "success", "failed", "retried", "dlq"];
  return (
    <WidgetGrid cols="md:grid-cols-5">
      {keys.map((k) => (
        <MetricCard
          key={k}
          label={k}
          value={counts[k] ?? 0}
          testId={`metric-count-${k}`}
          hint="live rolling"
        />
      ))}
    </WidgetGrid>
  );
}

function LatencyTable({ byJob = {} }) {
  const rows = Object.entries(byJob).sort(
    ([, a], [, b]) => (b?.p95 || 0) - (a?.p95 || 0)
  );
  if (rows.length === 0) {
    return <EmptyState message="Sin latencias registradas en la ventana." testId="metrics-latency-empty" />;
  }
  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="min-w-full text-xs" data-testid="metrics-latency-table">
        <thead className="border-b border-[var(--ag-border)] text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
          <tr>
            <th className="px-2 py-2 text-left">Job</th>
            <th className="px-2 py-2 text-right">count</th>
            <th className="px-2 py-2 text-right">p50</th>
            <th className="px-2 py-2 text-right">p95</th>
            <th className="px-2 py-2 text-right">p99</th>
            <th className="px-2 py-2 text-right">mean</th>
            <th className="px-2 py-2 text-right">max</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([jobName, stats]) => (
            <tr
              key={jobName}
              data-testid={`metrics-latency-row-${jobName}`}
              className="border-b border-[var(--ag-border)]/60 font-mono text-[10px] tabular-nums"
            >
              <td className="px-2 py-1.5 text-left">{jobName}</td>
              <td className="px-2 py-1.5 text-right">{stats.count}</td>
              <td className="px-2 py-1.5 text-right">{stats.p50}</td>
              <td className={`px-2 py-1.5 text-right ${_latencyColor(stats.p95)}`}>{stats.p95}</td>
              <td className="px-2 py-1.5 text-right">{stats.p99}</td>
              <td className="px-2 py-1.5 text-right">{stats.mean}</td>
              <td className="px-2 py-1.5 text-right">{stats.max}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export default function MetricsTab() {
  const q = useAutomationMetrics();
  const live = q.data?.live;

  return (
    <section className="space-y-4" data-testid="automation-metrics-tab">
      <WidgetCard
        title="Counters live"
        description="Ventana rolling · agregado backend Metrics · auto-refresh 30s."
        citations={["/api/automation/metrics"]}
        testId="widget-metrics-counters"
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar." />}
        {live && <CountersStrip counts={live.counts || {}} />}
      </WidgetCard>

      <WidgetCard
        title="Latencia por job"
        description="p50 / p95 / p99 · ordenado por p95 desc · colores según umbral (verde <500ms · amarillo <2s · rojo >2s)."
        citations={["/api/automation/metrics · latency_ms_by_job"]}
        testId="widget-metrics-latency"
      >
        {q.isLoading && <LoadingState />}
        {live && <LatencyTable byJob={live.latency_ms_by_job || {}} />}
      </WidgetCard>

      <WidgetGrid cols="md:grid-cols-3">
        <MetricCard
          label="Worker procesado"
          value={q.data?.worker_processed ?? 0}
          testId="metrics-worker-processed"
        />
        <MetricCard
          label="Scheduler tick count"
          value={q.data?.scheduler_tick_count ?? 0}
          testId="metrics-scheduler-tick-count"
        />
        <MetricCard
          label="Engine"
          value={q.data?.engine_running ? "RUNNING" : "STOPPED"}
          testId="metrics-engine-status"
        />
      </WidgetGrid>

      {q.data?.latest_snapshot && (
        <WidgetCard
          title="Último snapshot persistido"
          citations={["automation_metrics collection · 5-min interval"]}
          testId="widget-metrics-snapshot"
        >
          <div className="text-[11px]">
            <div>
              Guardado: <AbsoluteTime iso={q.data.latest_snapshot.snapshot_at} />
              {" · "}
              <RelativeTime iso={q.data.latest_snapshot.snapshot_at} />
            </div>
            <div className="text-[10px] text-[var(--ag-text-dim)]">
              ventana: <span className="font-mono">{q.data.latest_snapshot.window_seconds}s</span>
            </div>
          </div>
        </WidgetCard>
      )}

      <div className="text-[10px] text-[var(--ag-text-dim)]" data-testid="metrics-tick-hint">
        Scheduler last tick:{" "}
        <RelativeTime iso={q.data?.scheduler_last_tick_at} />
      </div>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["snapshot cada 5 min", "rolling window 1h"]} />
      </footer>
    </section>
  );
}
